import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Table,
  TextInput,
  Text,
  Title,
  Paper,
} from '@mantine/core'
import {
  usePlanoModeloCopiar,
  usePlanoModeloDelete,
  usePlanoModeloList,
} from '../../hooks/usePlanosModelo'
import {
  CATEGORIAS_PLANO,
  TIPOS_EQUIPAMENTO,
  TIPOS_PLANO,
  labelDe,
} from './constants'
import PlanoModeloFormModal from './PlanoModeloFormModal'
import AtividadesDrawer from './AtividadesDrawer'
import { extractApiError } from '../../lib/extractApiError'
import type { PlanoModelo } from '../../types/pmocPlanos'

export default function PlanosModeloPage() {
  const [tipoPlano, setTipoPlano] = useState<string | null>(null)
  const [categoria, setCategoria] = useState<string | null>(null)
  const [tipoEquip, setTipoEquip] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [somenteAtivos, setSomenteAtivos] = useState(true)

  const [modalAberto, setModalAberto] = useState(false)
  const [planoEdicao, setPlanoEdicao] = useState<PlanoModelo | null>(null)
  const [drawerPlano, setDrawerPlano] = useState<PlanoModelo | null>(null)

  const lista = usePlanoModeloList({
    tipo_plano: tipoPlano ?? undefined,
    categoria: categoria ?? undefined,
    tipo_equipamento: tipoEquip ?? undefined,
    ativo: somenteAtivos ? true : undefined,
    search: search.trim() || undefined,
  })
  const remove = usePlanoModeloDelete()
  const copiar = usePlanoModeloCopiar()

  async function handleCopiar(p: PlanoModelo) {
    try {
      const novo = await copiar.mutateAsync(p.id)
      notifications.show({
        color: 'green',
        message: `Cópia criada como "${novo.nome}" (personalizado).`,
      })
      setPlanoEdicao(novo)
      setModalAberto(true)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao copiar',
        message: extractApiError(err, 'Erro ao copiar plano.'),
      })
    }
  }

  async function handleRemover(p: PlanoModelo) {
    if (
      !confirm(
        `Inativar plano "${p.nome}"? Snapshots em PMOCs já aplicados não são afetados.`,
      )
    )
      return
    try {
      await remove.mutateAsync(p.id)
      notifications.show({ color: 'green', message: 'Plano inativado.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao inativar.'),
      })
    }
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Planos Modelo</Title>
          <Text size="sm" c="dimmed">
            Biblioteca reutilizável de planos técnicos. Aplique a um PMOC para criar
            o snapshot imutável (PlanoPMOC).
          </Text>
        </div>
        <Button
          onClick={() => {
            setPlanoEdicao(null)
            setModalAberto(true)
          }}
          data-testid="planos-modelo-novo"
        >
          + Novo plano
        </Button>
      </Group>

      <Paper p="sm" withBorder mb="md">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <Select
            label="Tipo"
            placeholder="Todos"
            clearable
            data={TIPOS_PLANO}
            value={tipoPlano}
            onChange={setTipoPlano}
            w={180}
            data-testid="filtro-tipo-plano"
          />
          <Select
            label="Categoria"
            placeholder="Todas"
            clearable
            data={CATEGORIAS_PLANO}
            value={categoria}
            onChange={setCategoria}
            w={200}
            data-testid="filtro-categoria"
          />
          <Select
            label="Tipo equipamento"
            placeholder="Todos"
            clearable
            searchable
            data={TIPOS_EQUIPAMENTO}
            value={tipoEquip}
            onChange={setTipoEquip}
            w={220}
            data-testid="filtro-tipo-equipamento"
          />
          <TextInput
            label="Buscar"
            placeholder="Nome ou descrição"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={240}
            data-testid="filtro-search"
          />
          <Button
            variant={somenteAtivos ? 'filled' : 'default'}
            onClick={() => setSomenteAtivos((v) => !v)}
            data-testid="filtro-ativos"
          >
            {somenteAtivos ? 'Só ativos' : 'Mostrar todos'}
          </Button>
        </Group>
      </Paper>

      {lista.isLoading ? (
        <Text c="dimmed">Carregando planos…</Text>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <Paper p="lg" withBorder>
          <Text c="dimmed">
            Nenhum plano encontrado com os filtros atuais. Crie um plano modelo
            personalizado ou rode o seed inicial (
            <code>manage.py seed_planos_pmoc_iniciais</code>) para popular o catálogo.
          </Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Origem</Table.Th>
                <Table.Th>Tipos equip.</Table.Th>
                <Table.Th>Atividades</Table.Th>
                <Table.Th>Versão</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lista.data?.results.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={600}>{p.nome}</Text>
                      {p.descricao && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {p.descricao}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>{labelDe(p.tipo_plano, TIPOS_PLANO)}</Table.Td>
                  <Table.Td>{labelDe(p.categoria, CATEGORIAS_PLANO)}</Table.Td>
                  <Table.Td>{p.origem}</Table.Td>
                  <Table.Td>
                    {p.tipos_equipamento.length === 0
                      ? '—'
                      : p.tipos_equipamento.length > 2
                        ? `${p.tipos_equipamento.length} tipos`
                        : p.tipos_equipamento
                            .map((t) => labelDe(t, TIPOS_EQUIPAMENTO))
                            .join(', ')}
                  </Table.Td>
                  <Table.Td>{p.atividades.length}</Table.Td>
                  <Table.Td>v{p.versao}</Table.Td>
                  <Table.Td>
                    {p.ativo ? (
                      <Badge color="green">Ativo</Badge>
                    ) : (
                      <Badge color="gray">Inativo</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => setDrawerPlano(p)}
                      >
                        Atividades
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => {
                          setPlanoEdicao(p)
                          setModalAberto(true)
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => handleCopiar(p)}
                        loading={copiar.isPending}
                      >
                        Copiar
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => handleRemover(p)}
                        loading={remove.isPending}
                      >
                        Inativar
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <PlanoModeloFormModal
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        plano={planoEdicao}
      />

      <AtividadesDrawer
        plano={drawerPlano}
        onClose={() => setDrawerPlano(null)}
      />
    </div>
  )
}
